import React, { useCallback, useMemo, useRef } from "react";
import { useSelector } from "react-redux";
import PropTypes from "prop-types";

import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import { makeStyles } from "tss-react/mui";

import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";

import KMKGroupSelect from "../../../../../../Common/KMKGroupSelect";
import ComponentSuspense from "../../../../../../Common/ComponentSuspense";
import { checkIsClusterExist, checkIsSchemaExist, setPrevState } from "../../../../../../../util/common";

const MyAceEditor = React.lazy(() => import("../../../../../../Common/AceEditor"));

const useStyles = makeStyles()(() => ({
  queryArea: {
    width: "100% !important",
    height: "200px !important",
    border: "1px solid",
    fontSize: "0.7rem",
    "&::selection": { background: "#B9B9B9", color: "#000000" },
    "&::-moz-selection": { background: "#B9B9B9", color: "#000000" },
    "&::-webkit-selection": { background: "#B9B9B9", color: "#000000" },
  },
  AutoComplete: {
    "& .MuiAutocomplete-listbox": {
      maxWidth: "100%",
      wordBreak: "break-all !important",
    },
  },
  MenuItem: {
    whiteSpace: "normal",
    wordBreak: "break-word",
  },
}));

const CustomPaper = React.memo((props) => {
  return (
    <Paper
      {...props}
      sx={{
        "& .MuiAutocomplete-listbox": {
          maxWidth: "100%",
          wordBreak: "break-all !important",
        },
      }}
    />
  );
});

const Types = [{ value: "SQL", text: "SQL" }];

const TransformPanel = (props) => {
  const {
    transformJobState,
    setTransformJobState,
    runJobAsUserId,
    schemaError,
    setSchemaError,
    schemas,
    fieldLoading,
    getSchemaList,
    cluster,
    setRunTimeState,
    getExternalSnowflakeCluster,
  } = props;

  const { classes } = useStyles();
  const queryRef = useRef(null);

  const workspace = useSelector((state) => state.workspace);
  const dataPipeline = useSelector((state) => state.dataPipeline);
  const config = useSelector((state) => state.config);
  const clusterList = useSelector((state) => state.clusterList);
  const {
    editJob: { id: Job_id },
  } = useMemo(() => dataPipeline, [dataPipeline]);
  const { DBName } = workspace.workspace;
  const { defaultSchema, defaultCluster } = config;

  const { Type, location, schema, database, queryLastUpdatedByName, query } = useMemo(
    () => transformJobState,
    [transformJobState]
  );

  const locationType = useMemo(
    () => transformJobState?.locationList.find(({ value }) => value === transformJobState.location)?.type || "",
    [transformJobState.location, transformJobState?.locationList]
  );

  const handleConnectionChange = useCallback(
    async (location) => {
      setPrevState(setTransformJobState, { location });

      const locationType = transformJobState?.locationList.find(({ value }) => value === location)?.type || "";
      if (locationType === "snowflake") {
        const [schemasByDB, clusters] = await Promise.all([
          getSchemaList(location),
          location !== "SNOWFLAKE" ? getExternalSnowflakeCluster(location) : clusterList,
        ]);

        const isSchemaExist = checkIsSchemaExist(schemasByDB, transformJobState.database, transformJobState.schema);
        if (!isSchemaExist)
          setPrevState(setTransformJobState, {
            ...(location !== "SNOWFLAKE" ? { database: "", schema: "" } : { database: DBName, schema: defaultSchema }),
          });

        const isClusterExist = checkIsClusterExist(clusters, cluster);

        setPrevState(setRunTimeState, {
          cluster: isClusterExist ? cluster : location === "SNOWFLAKE" ? defaultCluster : "",
        });
      }
    },
    [
      DBName,
      cluster,
      clusterList,
      defaultCluster,
      defaultSchema,
      getExternalSnowflakeCluster,
      getSchemaList,
      setRunTimeState,
      setTransformJobState,
      transformJobState.database,
      transformJobState?.locationList,
      transformJobState.schema,
    ]
  );

  const handleSchemaChange = useCallback(
    (e, value) => {
      setPrevState(setTransformJobState, { schema: value?.title || "", database: value?.groupBy || "" });
      setSchemaError((prevState) => (prevState ? !prevState : prevState));
    },
    [setSchemaError, setTransformJobState]
  );

  return (
    <>
      <Grid container direction={"row"} spacing={2} sx={{ mb: "16px" }}>
        <Grid sx={{ width: { xs: "100%", sm: "300px" } }}>
          <FormControl fullWidth>
            <InputLabel required>Connection</InputLabel>
            <Select
              value={location}
              label="Connection"
              required
              sx={{ wordBreak: "break-all" }}
              MenuProps={{
                slotProps: { paper: { style: { maxHeight: "220px", maxWidth: "280px" } } },
              }}
              onChange={(e) => handleConnectionChange(e.target.value)}
            >
              {transformJobState?.locationList.map((option) => (
                <MenuItem key={option.value} value={option.value} className={classes.MenuItem}>
                  {option.text}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid sx={{ width: { xs: "100%", sm: "300px" } }}>
          <FormControl fullWidth>
            <InputLabel required>Type</InputLabel>
            <Select
              value={Type}
              label="Type"
              required
              MenuProps={{ sx: { height: "220px" } }}
              onChange={(e) => {
                setPrevState(setTransformJobState, { Type: e.target.value });
              }}
            >
              {Types.map((option) => (
                <MenuItem key={option.value} value={option.value} className={classes.MenuItem}>
                  {option.text}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        {locationType === "snowflake" && (
          <Grid sx={{ width: { xs: "100%", sm: "300px" } }}>
            <KMKGroupSelect
              loading={fieldLoading}
              onOpen={
                schemas === null
                  ? () => {
                      if (fieldLoading) return;
                      getSchemaList(transformJobState.location);
                    }
                  : () => {}
              }
              options={schemas || []}
              label="Schema"
              value={{ title: schema, groupBy: database }}
              handleChange={handleSchemaChange}
              error={schemaError}
              errorText={"Schema does not exists or you do not have access to work on it."}
              noOptionsText={"No schema found"}
              className={classes.AutoComplete}
              PaperComponent={CustomPaper}
              fullWidth
              required
            />
          </Grid>
        )}
      </Grid>
      <Grid container>
        <Grid sx={{ paddingTop: "10px !important", display: "flex" }} size={12}>
          <Typography color={"#00000099"} padding={0} variant="subtitle2" component="span">
            {Job_id ? `Query * (Last updated by: ${queryLastUpdatedByName || "-"})` : `Query *`}
          </Typography>
        </Grid>
        <Grid sx={{ m: 0, my: 1 }} size={12}>
          <ComponentSuspense>
            <MyAceEditor
              fontSize={14}
              className={classes.queryArea}
              ref={queryRef}
              label="Query"
              value={query}
              setQuery={(value) => setPrevState(setTransformJobState, { query: value })}
              location={location}
              type={locationType?.toLowerCase() || ""}
              runParams={{
                runJobAsUserId: Job_id && !runJobAsUserId ? null : runJobAsUserId,
                database: database,
                schema: schema,
                cluster,
              }}
              queryRestriction={{ onlyOneQuery: false, onlySelectQuery: false }}
              queryLastUpdatedByName={queryLastUpdatedByName}
            />
          </ComponentSuspense>
        </Grid>
      </Grid>
    </>
  );
};

TransformPanel.propTypes = {
  transformJobState: PropTypes.object.isRequired,
  setTransformJobState: PropTypes.func.isRequired,
  schemaError: PropTypes.string.isRequired,
  setSchemaError: PropTypes.func.isRequired,
  schemas: PropTypes.array.isRequired,
  fieldLoading: PropTypes.bool.isRequired,
  getSchemaList: PropTypes.func.isRequired,
};

export default React.memo(TransformPanel);
